/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { GridContainer, GridItem, Link, LoadingOverlay } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import SignIn, { SignInStyles } from "@monsma/App/Components/SignIn/SignIn";
import XPopup from "@monsma/XDL/Components/XPopup";
import { screen } from "@monsma/XDL/Helpers";
import SignOutAlt from "@monsma/XDL/Icons/SignOutAlt";
import SortArrow from "@monsma/XDL/Icons/SortArrow";
import UserCircle from "@monsma/XDL/Icons/UserCircle";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        [fields.links]: LinkModel[];
    };
}

const getAllowedLinks = (state: ApplicationState, ownProps: OwnProps) => {
    return (ownProps.fields.links || []).map((linkItem: LinkModel) => {
        const node = getPageLinkByNodeId(state, linkItem?.fields?.destination?.value);

        return {
            url: node?.url || "",
            excludeFromNavigation: node?.excludeFromNavigation,
            title: linkItem.fields.overriddenTitle || node?.title,
            pageType: node?.type,
        };
    });
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    const { search } = getLocation(state);

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        links: getAllowedLinks(state, ownProps),
        session: state.context.session,
        selectedBillToId: state.context.session.billToId,
        billToState: getBillToState(state, state.context.session.billToId),
        showLoadingOverlay: state.pages.signIn.isSigningInAsGuest || state.context.isSigningIn,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    loadBillTo,
};

export interface HeaderSignInStyles {
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    xPopupTitle?: TypographyPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    createNewAccountButtonGridContainer?: GridContainerProps;
    createNewAccountButtonGridItem?: GridItemProps;
    myAccountXPopupContent?: InjectableCss;
    signInXPopupContent?: InjectableCss;
    myAccountLinksWrapper?: InjectableCss;
    myAccountLinksTitle?: InjectableCss;
    myAccountLinks?: InjectableCss;
    myAccountLinkWrapper?: InjectableCss;
    profileInfoSalesRepGridContainer?: GridContainerProps;
    profileInfoGridItem?: GridItemProps;
    salesRepGridItem?: GridItemProps;
    profileInfoSalesRepTitle?: InjectableCss;
    profileSalesInfoWrapper?: InjectableCss;
    profileSalesInfo?: InjectableCss;
    signOutIcon?: IconPresentationProps;
    titleIconUpDown?: IconPresentationProps;
    titleText?: TypographyPresentationProps;
    inventoryToggleClickable?: ClickablePresentationProps;
    pricingToggleClickable?: ClickablePresentationProps;
    inventoryToggleIcon?: IconPresentationProps;
    pricingToggleIcon?: IconPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    titleClickable: {
        css: css`
            height: 50px;
            font-size: 14px;
            line-height: 20px;
            font-weight: 700;
            color: ${getColor("common.ui10")};
            padding: 0 15px;
            background-size: 100% 200%;
            background-image: linear-gradient(to bottom, transparent 50%, ${getColor("common.ui4")} 50%);
            transition: all 200ms ease-in-out;

            * {
                fill: ${getColor("common.ui9")};
            }

            ${screen("gt", "md")} {
                &.active,
                &:hover,
                &:focus-within {
                    background-position: 0 100%;
                    color: ${getColor("common.background")};

                    &&& * {
                        color: ${getColor("common.background")};
                        fill: ${getColor("common.background")};
                    }
                }
            }

            ${screen("lte", "md")} {
                padding: 5px;
                height: auto;
                margin: 17px 7px;
            }
        `,
    },
    titleText: {
        css: css`
            ${screen("lte", "md")} {
                display: none;
            }
        `,
    },
    titleIcon: {
        size: 14,
        color: "common.ui9",
        css: css`
            margin-right: 10px;
            transition: all 0.2s linear;

            ${screen("lte", "md")} {
                margin-right: 0;

                &,
                > svg {
                    width: 22px;
                    height: 22px;
                }
            }
        `,
    },
    titleIconUpDown: {
        size: 8,
        css: css`
            margin-left: 5px;
            transition: fill 0.2s linear;

            ${screen("lte", "md")} {
                display: none;
            }
        `,
    },
    xPopupTitle: {
        css: css`
            font-size: 16px;
            line-height: 20px;
            font-weight: 700;
            margin-bottom: 22px;
            color: ${getColor("common.ui10")};
        `,
    },
    createNewAccountButtonGridContainer: {
        gap: 0,
    },
    createNewAccountButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    createNewAccountButton: {
        variant: "secondary",
    },
    myAccountXPopupContent: {
        css: css`
            width: 494px;

            ${screen("lte", "sm")} {
                width: 100vw;
            }
        `,
    },
    signInXPopupContent: {
        css: css`
            width: 300px;

            ${screen("lte", "xs")} {
                width: 100vw;
            }
        `,
    },
    myAccountLinksTitle: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            font-weight: 700;
            margin-bottom: 15px;
            color: ${getColor("common.ui10")};
        `,
    },
    myAccountLinkWrapper: {
        css: css`
            width: 50%;
            margin-top: 6px;
            font-size: 13px;
            line-height: 22px;

            &:nth-child(1),
            &:nth-child(2) {
                margin-top: 0;
            }
        `,
    },
    myAccountLinks: {
        css: css`
            display: flex;
            flex-flow: row wrap;
        `,
    },
    myAccountLinksWrapper: {
        css: css`
            margin-bottom: 30px;
        `,
    },
    profileInfoSalesRepGridContainer: {
        gap: 0,
        css: css`
            margin-bottom: 40px;
        `,
    },
    profileInfoGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    salesRepGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    profileInfoSalesRepTitle: {
        css: css`
            font-size: 14px;
            line-height: 20px;
            font-weight: 700;
            color: ${getColor("common.ui10")};
            margin-bottom: 15px;
        `,
    },
    profileSalesInfo: {
        css: css`
            font-size: 13px;
            line-height: 22px;
            color: ${getColor("text.main")};
            margin-bottom: 6px;

            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    signOutIcon: {
        size: 14,
        color: "common.ui5",
        css: css`
            margin-right: 5px;
        `,
    },
    signOutClickable: {
        css: css`
            font-size: 12px;
            font-weight: 600;
            line-height: 15px;
            color: ${getColor("common.ui2")};
            transition: all 0.2s linear;

            &:hover,
            &:focus {
                color: ${getColor("common.ui3")};
            }
        `,
    },
    inventoryToggleClickable : {
        css: css`
            font-size: 12px;
            font-weight: 600;
            line-height: 15px;
            color: ${getColor("common.ui2")};
            transition: all 0.2s linear;
            margin-bottom: 6px;

            &:hover,
            &:focus {
                color: ${getColor("common.ui3")};
            }
        `,
    },
    pricingToggleClickable : {
        css: css`
            font-size: 12px;
            font-weight: 600;
            line-height: 15px;
            color: ${getColor("common.ui2")};
            transition: all 0.2s linear;
            margin-bottom: 6px;

            &:hover,
            &:focus {
                color: ${getColor("common.ui3")};
            }
        `,
    },
    inventoryToggleIcon : {
        size: 14,
        color: "common.ui5",
        css: css`
            margin-right: 5px;
        `,
    },
    pricingToggleIcon : {
        size: 14,
        color: "common.ui5",
        css: css`
            margin-right: 5px;
        `,
    },
};

export const SignInComponentStyles: SignInStyles = {
    signInGridContainer: {
        gap: 0,
    },
    form: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    userNameGridItem: {
        css: css`
            margin-bottom: 20px !important;
        `,
    },
    passwordGridItem: {
        css: css`
            margin-bottom: 25px !important;
        `,
    },
    forgotPasswordLink: {
        css: css`
            top: 1px;
            right: 0;
        `,
    },
    rememberMeGridItem: {
        css: css`
            margin-bottom: 26px !important;
        `,
    },
    signInButton: {
        css: css`
            width: 100% !important;
        `,
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    createAccountUrl,
    links,
    billToState,
    selectedBillToId,
    session,
    loadBillTo,
    showLoadingOverlay,
}: Props) => {
    let icon: React.ComponentType<IconPresentationProps>;

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && selectedBillToId && session.isAuthenticated) {
            loadBillTo({ billToId: selectedBillToId });
        }
    }, []);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    const authorized = userName && !currentUserIsGuest;

    if (fields.icon === "User") {
        icon = UserCircle;
    }

    const onSignInHandler = () => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const titleClickable = (title: string, dataTestSelector: string, clickHandler: any) => {
        return (
            <Clickable {...styles.titleClickable} onClick={clickHandler} data-test-selector={dataTestSelector}>
                {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                {showLabel && <Typography {...styles.titleText}>{translate(`${title}`)}</Typography>}
                {showIcon && <Icon {...styles.titleIconUpDown} src={SortArrow} />}
            </Clickable>
        );
    };

    const xPopupTrigger = (
        <>
            {!authorized && titleClickable("Sign In / Register", "header_signIn", onSignInHandler)}
            {authorized &&
                titleClickable("My Account", "headerMyAccountLink", () =>
                    history.push(myAccountPageLink?.url as string),
                )}
        </>
    );

    const xPopupContent = (
        <>
            <LoadingOverlay
                loading={showLoadingOverlay}
                css={css`
                    width: 100%;
                `}
            >
                {authorized && (
                    <StyledWrapper {...styles.myAccountXPopupContent}>
                        <Typography as="h5" {...styles.xPopupTitle}>
                            {translate("Hello")}, {session.firstName || session.userName || session.email}
                        </Typography>
                        <StyledWrapper {...styles.myAccountLinksWrapper}>
                            <Typography as="h6" {...styles.myAccountLinksTitle}>
                                {siteMessage("Custom_MyAccountPopup_Title")}
                            </Typography>
                            <StyledWrapper {...styles.myAccountLinks}>
                                {links.map((link: any) => {
                                    return (
                                        <StyledWrapper key={uniqueId()} {...styles.myAccountLinkWrapper}>
                                            <Link href={link.url} className="close-menu">
                                                {link.title}
                                            </Link>
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                        <GridContainer {...styles.profileInfoSalesRepGridContainer}>
                            <GridItem {...styles.profileInfoGridItem}>
                                <Typography as="h6" {...styles.profileInfoSalesRepTitle}>
                                    {siteMessage("Custom_MyAccountPopup_ProfileInfoTitle")}
                                </Typography>
                                <StyledWrapper {...styles.profileSalesInfoWrapper}>
                                    <Typography as="div" {...styles.profileSalesInfo}>
                                        {session?.firstName}&nbsp;{session?.lastName}
                                    </Typography>
                                    <Typography as="div" {...styles.profileSalesInfo}>
                                        {billToState.value?.companyName}
                                    </Typography>
                                    <Typography as="div" {...styles.profileSalesInfo}>
                                        {billToState.value?.customerNumber}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                            <GridItem {...styles.salesRepGridItem}>
                                <Typography as="h6" {...styles.profileInfoSalesRepTitle}>
                                    {siteMessage("Custom_MyAccountPopup_SalesRepTitle")}
                                </Typography>
                                <StyledWrapper {...styles.profileSalesInfoWrapper}>
                                    {session?.properties?.salesRepName && (
                                        <Typography as="div" {...styles.profileSalesInfo}>
                                            {session?.properties?.salesRepName}
                                        </Typography>
                                    )}
                                    {session?.properties?.salesRepEmail && (
                                        <Typography as="div" {...styles.profileSalesInfo}>
                                            <Link href={`mailto:${session?.properties?.salesRepEmail}`}>
                                                {session?.properties?.salesRepEmail}
                                            </Link>
                                        </Typography>
                                    )}
                                    {session?.properties?.salesRepPhone && (
                                        <Typography as="div" {...styles.profileSalesInfo}>
                                            {session?.properties?.salesRepPhone}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        </GridContainer>
                        {showInventoryToggle && displayPricingAndInventory && (
                            <Clickable
                                {...styles.inventoryToggleClickable}
                                onClick={() => setShowInventory({ showInventory: !showInventory })}
                            >
                                <Icon {...styles.inventoryToggleIcon} src={showInventory ? EyeOff : Eye} />
                                {showInventory ? translate("Hide Inventory") : translate("Show Inventory")}
                            </Clickable>
                        )}
                        {showPricingToggle && displayPricingAndInventory && (
                            <Clickable
                                {...styles.pricingToggleClickable}
                                onClick={() => setShowPricing({ showPricing: !showPricing })}
                            >
                                <Icon {...styles.pricingToggleIcon} src={showPricing ? EyeOff : Eye} />
                                {showPricing ? translate("Hide Cost") : translate("Show Cost")}
                            </Clickable>
                        )}
                        <Clickable
                            {...styles.signOutClickable}
                            onClick={onSignOutHandler}
                            data-test-selector="headerSignOutLink"
                        >
                            <Icon {...styles.signOutIcon} src={SignOutAlt} />
                            {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    </StyledWrapper>
                )}
                {!authorized && (
                    <StyledWrapper {...styles.signInXPopupContent}>
                        <Typography as="h5" {...styles.xPopupTitle}>
                            {siteMessage("Custom_SignInPopup_Title")}
                        </Typography>
                        {/* 
                            xng-change: move the reset password modal to be inside the sign in to fix the issue that appears on the sign in headr
                            to keep the sign in pop up opened - MON-536
                        */}
                        <SignIn extendedStyles={SignInComponentStyles} />
                        <GridContainer {...styles.createNewAccountButtonGridContainer}>
                            <GridItem {...styles.createNewAccountButtonGridItem}>
                                <Button
                                    {...styles.createNewAccountButton}
                                    onClick={handleCreateNewAccount}
                                    data-test-selector="signInCreateNewAccountOverlay_createNewAccount"
                                >
                                    {translate("Register An Account")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </>
    );

    return (
        <>
            <XPopup trigger={xPopupTrigger} shade={true}>
                {xPopupContent}
            </XPopup>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    const overriddenTitle = item.fields.overriddenTitle;

                    if (overriddenTitle) {
                        return overriddenTitle;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 6,
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
